// src/components/SideBar/SideBar.jsx
import React, { useMemo, useState } from 'react';
import {
    Logout as LogoutIcon,
    ChevronLeft,
    Menu as MenuIcon,
    ChevronRight,
    ExpandLess,
    ExpandMore,
    Mail,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import i18next from 'i18next';

import { useTheme } from '@mui/material/styles';
import { Avatar, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Collapse from '@mui/material/Collapse';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// import DarkModeToggle from './../global/DarkModeToggle';
import { Drawer, DrawerHeader } from './UI';
import { sidebarItems } from '../../constants/sidebarItems';

import { useDarkMode } from '../../context/DarkModeContext';
import { useAuthContext } from '../../auth/useAuthContext';
import { useSettingsContext } from '../../setting/SettingsContext';

// مهم: هذا هو هوك الصلاحيات اللي أنشأناه (عدّل المسار حسب مكانه عندك)
import { usePermissions } from '../../auth/PermissionsContext';

const SideBar = ({ open, setOpen }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const navigate = useNavigate();
    const location = useLocation();

    const lng = i18next.language || 'en';
    const isArabic = lng === 'ar';

    const { toggleDarkMode } = useDarkMode();
    const { logout, user } = useAuthContext();
    const { hasPrefix, isInitialized, isAuthenticated } = usePermissions();
    const { themeNavColor } = useSettingsContext();
    const isApparent = themeNavColor === 'apparent';

    // Resolved sidebar palette — switches between the vivid primary
    // (Integrate, the legacy default) and the neutral surface (Apparent).
    const navBg = isApparent
        ? (isDark ? '#0F172A' : '#FFFFFF')
        : theme.palette.primary.main;
    const navBgImage = isApparent
        ? 'none'
        : (isDark
            ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`);
    const navText = isApparent
        ? (isDark ? '#E5E7EB' : '#1C252E')
        : (isDark ? '#E5E7EB' : '#020617');
    const navShadow = isApparent
        ? `0 2px 10px 0 ${isDark ? 'rgba(0,0,0,0.45)' : 'rgba(15,23,42,0.08)'}`
        : `0 2px 10px 0 ${theme.palette.primary.main}59`;

    const normalizePhotoUrl = (url) => {
        if (!url) return '';
        if (typeof url !== 'string') return url;
        try {
            const u = new URL(url);
            u.pathname = u.pathname.replace(/^\/api\//, '/');
            return u.toString();
        } catch (e) {
            return url.replace(/^\/?api\//, '/');
        }
    };

    // ربط سريع بين المسارات و prefix الصلاحيات حسب الـ permissions_names التي عندك
    // مثال: countries_list / countries_create ... => prefix = "countries_"
    const permissionPrefixByPath = useMemo(
        () => ({
            '/dashboard': null,
            '/profile': null,
            
            '/users': 'users',
            '/clients': 'clients',
            
            '/services': 'services',
            '/services/section': 'services',
            
            '/projects': 'projects',
            '/projects/section': 'projects',
            
            '/about': 'about',
            '/about-header/section': 'about_header',
            '/home-header/section': 'home_header',
            
            '/testimonials': 'testimonials',
            '/testimonials/section': 'testimonials',
            
            '/contacts/section': 'contacts',
            
            '/why-choose-us': 'why_choose_us',
            '/why-choose-us/section': 'why_choose_us',
            
            '/blog': 'blogs',
            '/blog/section': 'blogs',
            '/blog/categories': 'categories',
            '/blog/tags': 'tags',
            
            '/ugc/section': 'ugc',
            
            '/settings/general': 'settings',
            '/settings/roles': 'roles',
            '/settings/contact': 'settings',
        }),
        []
    );

    const filteredItems = useMemo(() => {
        const canSeeItem = (item) => {
            // If the path is explicitly mapped to null (like dashboard), show it.
            if (permissionPrefixByPath[item.path] === null) return true;
            
            const prefix = permissionPrefixByPath[item.path];
            if (!prefix) return true; // Show items with no mapped permissions (or no path)
            return hasPrefix(prefix);
        };

        return sidebarItems
            .map((item) => {
                const children = Array.isArray(item.children)
                    ? item.children.filter(canSeeItem)
                    : undefined;
                return { ...item, children };
            })
            .filter((item) => {
                if (Array.isArray(item.children)) {
                    return item.children.length > 0;
                }
                return canSeeItem(item);
            });
    }, [permissionPrefixByPath, hasPrefix]);

    const [openSubmenus, setOpenSubmenus] = useState({});
    const toggleSubmenu = (key) =>
        setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));

    const handleToggleDrawer = () => setOpen((prev) => !prev);

    const handleLogout = async () => {
        try {
            const response = await logout();
            const message = response?.data?.message || (isArabic ? 'تم تسجيل الخروج' : 'Logged out successfully');

            toast.success(message);
            navigate('/login');
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message || error?.message || (isArabic ? 'فشل تسجيل الخروج' : 'Logout failed');
            toast.error(errorMessage);
        }
    };

    // لو لم يتم تهيئة auth/permissions بعد، لا نعرض عناصر قد تومض/تظهر ثم تختفي
    const blockMenuUntilReady = !isInitialized || !isAuthenticated;

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <Drawer
                variant="permanent"
                open={open}
                anchor={isArabic ? 'right' : 'left'}
                sx={{
                    // width:240,
                    '& .MuiDrawer-paper': {
                        maxWidth: 240,
                        overflowX: 'hidden',
                        backgroundColor: navBg,
                        backgroundImage: navBgImage,
                        boxShadow: navShadow,
                        color: navText,
                        borderRight: !isArabic ? `1px solid ${theme.palette.divider}` : 'none',
                        borderLeft: isArabic ? `1px solid ${theme.palette.divider}` : 'none',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(148,163,184,0.8) transparent',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(148,163,184,0.6)',
                            borderRadius: 999,
                        },
                        '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(148,163,184,0.9)' },
                    },
                }}
            >
                <DrawerHeader sx={{ justifyContent: open ? 'flex-end' : 'center' }}>
                    <IconButton onClick={handleToggleDrawer} sx={{ color: '#E5E7EB' }}>
                        {open ? (isArabic ? <ChevronRight /> : <ChevronLeft />) : <MenuIcon />}
                    </IconButton>
                </DrawerHeader>

                {/* Profile */}
                <Divider sx={{ borderColor: 'rgba(148,163,184,0.35)' }} />
                <Box
                    sx={{
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: open ? 'flex-start' : 'center',
                        mt: 1.5,
                        mb: 1,
                        px: open ? 1 : 0,
                        cursor: 'pointer',
                    }}
                    onClick={() => navigate('/profile')}
                >
                    <Avatar
                        src={normalizePhotoUrl(user?.image?.thumb || user?.image)}
                        sx={{
                            width: 32,
                            height: 32,
                            boxShadow: '0 0 0 2px rgba(15,23,42,0.5)',
                        }}
                    />

                    {open && (
                        <Box sx={{ ml: isArabic ? 0 : 1.5, mr: isArabic ? 1.5 : 0 }}>
                            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: '#F9FAFB' }}>
                                <bdi>{user?.name ? `Hi ${user?.name}` : 'Hi'}</bdi>
                            </Typography>

                            <Typography noWrap sx={{ fontSize: 12, color: '#9CA3AF' }}>
                                {user?.email}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ borderColor: 'rgba(148,163,184,0.3)', mb: 1 }} />

                {/* Email row (اختياري) */}
                {open && user?.email && (
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            sx={{
                                minHeight: 42,
                                justifyContent: open ? 'initial' : 'center',
                                px: 1.5,
                                mx: open ? 0.5 : 0,
                                color: '#E5E7EB',
                                '&:hover': { bgcolor: 'rgba(148,163,184,0.18)' },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    ml: isArabic ? (open ? 2 : 0) : 0,
                                    mr: !isArabic ? (open ? 2 : 0) : 0,
                                    justifyContent: 'center',
                                    color: '#93C5FD',
                                }}
                            >
                                <Mail fontSize="small" />
                            </ListItemIcon>

                            <ListItemText
                                primary={user.email}
                                sx={{
                                    display: open ? 'block' : 'none',
                                    textAlign: isArabic ? 'right' : 'left',
                                    '& .MuiTypography-root': { fontSize: 12 },
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                )}

                {/* Sidebar items */}
                <List>
                    {!blockMenuUntilReady &&
                        filteredItems.map((item) => {
                            const Icon = item.icon;
                            const hasChildren = item.children && item.children.length > 0;
                            const childActive = hasChildren
                                ? item.children.some((ch) => location.pathname.startsWith(ch.path))
                                : false;
                            const active = location.pathname.startsWith(item.path) || childActive;
                            const submenuOpen = hasChildren
                                ? openSubmenus[item.key] ?? childActive
                                : false;

                            return (
                                <React.Fragment key={item.key}>
                                    <ListItem disablePadding sx={{ display: 'block' }}>
                                        <ListItemButton
                                            onClick={() =>
                                                hasChildren
                                                    ? toggleSubmenu(item.key)
                                                    : navigate(item.path)
                                            }
                                            sx={{
                                                minHeight: 44,
                                                justifyContent: open ? 'initial' : 'center',
                                                px: 1.5,
                                                mx: open ? 1 : 0,
                                                my: 0.25,
                                                color: '#E5E7EB',
                                                borderRadius: 1.5,
                                                bgcolor: active ? 'rgba(59,130,246,0.22)' : 'transparent',
                                                '&:hover': { bgcolor: 'rgba(59,130,246,0.18)' },
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    ml: isArabic ? (open ? 2 : 0) : 0,
                                                    mr: !isArabic ? (open ? 2 : 0) : 0,
                                                    justifyContent: 'center',
                                                    color: active ? '#93C5FD' : '#BFDBFE',
                                                }}
                                            >
                                                <Icon fontSize="small" />
                                            </ListItemIcon>

                                            <ListItemText
                                                primary={item.label?.[lng] || item.label?.en}
                                                sx={{
                                                    display: open ? 'block' : 'none',
                                                    textAlign: isArabic ? 'right' : 'left',
                                                    '& .MuiTypography-root': { fontSize: 14 },
                                                }}
                                            />

                                            {hasChildren && open && (
                                                submenuOpen ? <ExpandLess /> : <ExpandMore />
                                            )}
                                        </ListItemButton>
                                    </ListItem>

                                    {hasChildren && (
                                        <Collapse in={submenuOpen} timeout="auto" unmountOnExit>
                                            <List disablePadding>
                                                {item.children.map((child) => {
                                                    const ChildIcon = child.icon;
                                                    const childIsActive = location.pathname.startsWith(child.path);
                                                    return (
                                                        <ListItem key={child.key} disablePadding sx={{ display: 'block' }}>
                                                            <ListItemButton
                                                                onClick={() => navigate(child.path)}
                                                                sx={{
                                                                    minHeight: 40,
                                                                    justifyContent: open ? 'initial' : 'center',
                                                                    px: 2.5,
                                                                    mx: open ? 1.5 : 0,
                                                                    my: 0.15,
                                                                    color: '#E5E7EB',
                                                                    borderRadius: 1.2,
                                                                    bgcolor: childIsActive
                                                                        ? 'rgba(59,130,246,0.2)'
                                                                        : 'transparent',
                                                                    '&:hover': { bgcolor: 'rgba(59,130,246,0.15)' },
                                                                }}
                                                            >
                                                                <ListItemIcon
                                                                    sx={{
                                                                        minWidth: 0,
                                                                        ml: isArabic ? (open ? 2 : 0) : 0,
                                                                        mr: !isArabic ? (open ? 2 : 0) : 0,
                                                                        justifyContent: 'center',
                                                                        color: childIsActive ? '#93C5FD' : '#BFDBFE',
                                                                    }}
                                                                >
                                                                    {ChildIcon ? (
                                                                        <ChildIcon fontSize="small" />
                                                                    ) : (
                                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#93C5FD', display: 'inline-block' }} />
                                                                    )}
                                                                </ListItemIcon>

                                                                <ListItemText
                                                                    primary={child.label?.[lng] || child.label?.en}
                                                                    sx={{
                                                                        display: open ? 'block' : 'none',
                                                                        textAlign: isArabic ? 'right' : 'left',
                                                                        '& .MuiTypography-root': { fontSize: 13 },
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </Collapse>
                                    )}
                                </React.Fragment>
                            );
                        })}
                </List>

                <Divider sx={{ borderColor: 'rgba(148,163,184,0.3)', mt: 1, mb: 0.5 }} />

                {/* Logout */}
                <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                            px: 1.5,
                            mx: open ? 0.5 : 0,
                            color: '#FCA5A5',
                            '&:hover': { bgcolor: 'rgba(248,114,114,0.16)' },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                ml: isArabic ? (open ? 2 : 0) : 0,
                                mr: !isArabic ? (open ? 2 : 0) : 0,
                                justifyContent: 'center',
                                color: '#FCA5A5',
                            }}
                        >
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>

                        <ListItemText
                            primary={isArabic ? 'تسجيل الخروج' : 'Log Out'}
                            sx={{ display: open ? 'block' : 'none', '& .MuiTypography-root': { fontSize: 14 } }}
                        />
                    </ListItemButton>
                </ListItem>

                {/* Theme toggle */}
                <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                        onClick={toggleDarkMode}
                        sx={{
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                            px: 1.5,
                            mx: open ? 0.5 : 0,
                            color: '#E5E7EB',
                            '&:hover': { bgcolor: 'rgba(148,163,184,0.18)' },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                ml: isArabic ? (open ? 2 : 0) : 0,
                                mr: !isArabic ? (open ? 2 : 0) : 0,
                                justifyContent: 'center',
                                color: '#FACC15',
                            }}
                        >
                            {/* <DarkModeToggle /> */}
                        </ListItemIcon>

                        <ListItemText
                            primary={isArabic ? 'الوضع الليلي' : 'Theme'}
                            sx={{ display: open ? 'block' : 'none', '& .MuiTypography-root': { fontSize: 14 } }}
                        />
                    </ListItemButton>
                </ListItem>
            </Drawer>
        </Box>
    );
};

export default SideBar;
